/**
 * Physics-based candy drop for the bag-on-the-scale panel.
 * Uses Matter.js (self-hosted). 2 pieces per ounce per Ash's spec.
 * Each candy id gets a deterministic color so pieces are visually
 * distinguishable even before real product photography exists;
 * a real image (candy.image) is used as a texture when available.
 */

const CATEGORY_COLORS = {
  sour: '#9ED36A',
  sweet: '#F6C3D5',
  bubs: '#F7B52C',
  'salty-licorice': '#4B2C41',
  'foam-marshmallow': '#FFD3E1',
};

const IMAGE_SIZES = {};
const IMAGE_WAITERS = {};

const HALLOWEEN_COLORS = ['#FF7A1A', '#8A4FE8', '#9ED36A', '#3a2552', '#F7B52C', '#E8478B'];

function colorForCandy(candy) {
  if (candy.mode === 'halloween') {
    const n = String(candy.id).split('').reduce((s, ch) => s + ch.charCodeAt(0), 0);
    return HALLOWEEN_COLORS[n % HALLOWEEN_COLORS.length];
  }
  return CATEGORY_COLORS[candy.category] || '#D0155C';
}

class MixPhysics {
  constructor(canvas, candyById) {
    this.canvas = canvas;
    this.candyById = candyById;
    this.pieces = []; // [{ candyId, body }]

    const { Engine, Render, Runner, World, Bodies, Body } = Matter;
    this.Matter = Matter;

    this.engine = Engine.create({ gravity: { y: 1.1 } });
    this.engine.positionIterations = 10;
    this.engine.velocityIterations = 10;
    this.world = this.engine.world;

    const width = canvas.clientWidth || 280;
    const height = canvas.clientHeight || 220;
    this.width = width;
    this.height = height;

    this.render = Render.create({
      canvas,
      engine: this.engine,
      options: {
        width,
        height,
        wireframes: false,
        background: 'transparent',
        // draw at the screen's real pixel density (phones are 2-3x), otherwise the candy looks blurry; the world still works in CSS pixels
        pixelRatio: Math.min(window.devicePixelRatio || 1, 3),
      },
    });
    // Matter pins the canvas to a fixed pixel size when the ratio is not 1; hand sizing back to the stylesheet so the machine stays responsive
    canvas.style.removeProperty('width');
    canvas.style.removeProperty('height');

    // Walls follow the .bag__outline SVG path silhouette (traced as
    // fractions of width/height, matching its 0-100 viewBox / 100) so the
    // pile stays inside the drawn taper as it fills, instead of a
    // straight-sided box. Each segment is extended past its own endpoints
    // so consecutive segments overlap at the joint — a thinner
    // un-overlapped version let pieces tunnel through the seams.
    const thickness = Math.max(16, width * 0.05);
    const wallOptions = { isStatic: true, friction: 0.5, render: { visible: false } };
    const leftPoints = [
      [0.19, 0.26], [0.174, 0.48], [0.164, 0.76], [0.17, 0.91], [0.205, 0.965],
    ];
    const rightPoints = [
      [0.81, 0.26], [0.826, 0.48], [0.836, 0.76], [0.83, 0.91], [0.795, 0.965],
    ];
    const segments = [];
    const addChain = (points) => {
      for (let i = 0; i < points.length - 1; i++) {
        const x1 = points[i][0] * width, y1 = points[i][1] * height;
        const x2 = points[i + 1][0] * width, y2 = points[i + 1][1] * height;
        const dx = x2 - x1, dy = y2 - y1;
        const len = Math.hypot(dx, dy);
        const ux = dx / len, uy = dy / len;
        // extend half a thickness past each end so this segment overlaps
        // the next one at the joint, regardless of the angle between them
        const ex1 = x1 - (ux * thickness) / 2, ey1 = y1 - (uy * thickness) / 2;
        const ex2 = x2 + (ux * thickness) / 2, ey2 = y2 + (uy * thickness) / 2;
        const cx = (ex1 + ex2) / 2, cy = (ey1 + ey2) / 2;
        const extLen = Math.hypot(ex2 - ex1, ey2 - ey1);
        const angle = Math.atan2(dy, dx);
        segments.push(Bodies.rectangle(cx, cy, extLen, thickness, { ...wallOptions, angle }));
      }
    };
    addChain(leftPoints);
    addChain(rightPoints);
    const floor = Bodies.rectangle(
      width / 2, height * 0.965 + thickness / 2 - 4, width * 0.59, thickness, wallOptions
    );
    World.add(this.world, [...segments, floor]);
    this.bagInsetLeft = 0.164 * width;
    this.bagInsetRight = 0.836 * width;

    // Hard safety net: walls can still fail (tunneling under pile pressure,
    // canvas resizing after construction, etc.) — so on every physics step,
    // forcibly clamp each piece to the bag's real boundary at its own
    // height. This guarantees pieces are never visually outside the bag,
    // regardless of any wall-geometry edge case.
    this._boundaryLeftPts = leftPoints.map(([xf, yf]) => [xf * width, yf * height]);
    this._boundaryRightPts = rightPoints.map(([xf, yf]) => [xf * width, yf * height]);
    Matter.Events.on(this.engine, 'afterUpdate', () => this._clampPieces());

    Render.run(this.render);
    this.runner = Runner.create();
    Runner.run(this.runner, this.engine);

    this.destroyed = false;
    this.imageSizeCache = IMAGE_SIZES; // shared across rebuilds so a resized world never re-loads textures
    Object.values(candyById).forEach((candy) => {
      (candy.resolvedImages || []).forEach((url) => this._preloadImage(url));
    });
  }

  _boundAtY(points, y) {
    for (let i = 0; i < points.length - 1; i++) {
      const [x1, y1] = points[i], [x2, y2] = points[i + 1];
      if (y >= Math.min(y1, y2) && y <= Math.max(y1, y2)) {
        const t = (y - y1) / (y2 - y1 || 1);
        return x1 + t * (x2 - x1);
      }
    }
    return y < points[0][1] ? points[0][0] : points[points.length - 1][0];
  }

  _clampPieces() {
    const { Body } = this.Matter;
    const floorY = this.height * 0.965;
    this.pieces.forEach(({ body }) => {
      const radius = (body.circleRadius || 10) * 1.15; // sprites draw ~15% past the collision circle
      const y = Math.min(body.position.y, floorY - radius * 0.3);
      const leftB = this._boundAtY(this._boundaryLeftPts, y) + radius;
      const rightB = this._boundAtY(this._boundaryRightPts, y) - radius;
      let { x } = body.position;
      let clamped = false;
      if (x < leftB) { x = leftB; clamped = true; }
      if (x > rightB) { x = rightB; clamped = true; }
      let clampedY = body.position.y;
      if (clampedY > floorY - radius * 0.3) { clampedY = floorY - radius * 0.3; clamped = true; }
      if (clamped) {
        Body.setPosition(body, { x, y: clampedY });
        Body.setVelocity(body, { x: body.velocity.x * 0.3, y: Math.min(body.velocity.y, 0) });
      }
    });
  }

  _preloadImage(url, cb) {
    if (IMAGE_SIZES[url]) { if (cb) cb(IMAGE_SIZES[url]); return; }
    if (cb) (IMAGE_WAITERS[url] = IMAGE_WAITERS[url] || []).push(cb);
    if (IMAGE_WAITERS[url] && IMAGE_WAITERS[url].loading) return;
    IMAGE_WAITERS[url] = IMAGE_WAITERS[url] || [];
    IMAGE_WAITERS[url].loading = true;
    const img = new Image();
    const flush = (size) => {
      const waiters = IMAGE_WAITERS[url] || [];
      IMAGE_WAITERS[url] = [];
      waiters.forEach((fn) => fn(size));
    };
    img.onload = () => {
      IMAGE_SIZES[url] = { width: img.naturalWidth, height: img.naturalHeight };
      flush(IMAGE_SIZES[url]);
    };
    img.onerror = () => flush(null);
    img.src = url;
  }

  drop(candyId, count) {
    const candy = this.candyById[candyId];
    if (!candy) return;
    const variants = candy.resolvedImages || [];

    for (let i = 0; i < count; i++) {
      // pick a random variant per piece so multi-color candies (e.g.
      // Ahlgrens: green/pink/white) show a mix, not just one color
      const imageUrl = variants.length ? variants[Math.floor(Math.random() * variants.length)] : null;
      if (imageUrl && !IMAGE_SIZES[imageUrl]) {
        // wait for the texture instead of dropping a flat-colored dot
        this._preloadImage(imageUrl, (size) => {
          if (!this.destroyed) this._spawn(candyId, candy, imageUrl, size);
        });
      } else {
        this._spawn(candyId, candy, imageUrl, imageUrl ? IMAGE_SIZES[imageUrl] : null);
      }
    }
  }

  _spawn(candyId, candy, imageUrl, size) {
    const { Bodies, World } = this.Matter;
    const color = colorForCandy(candy);
    // size pieces so a full 15 oz bag (30 pieces) fills the bag's interior
    const baseR = Math.sqrt((this.width * this.height * 0.62 * 0.62 * 0.72) / (30 * Math.PI));
    const radius = baseR * (0.92 + Math.random() * 0.16);
    const margin = radius + 4;
    // spawn within the neck width (narrower than the base) so pieces
    // start inside the opening, not already past the taper
    const spawnLo = this.width * 0.19 + margin;
    const spawnHi = this.width * 0.81 - margin;
    const x = spawnLo + Math.random() * Math.max(1, spawnHi - spawnLo);
    const render = size
      ? {
          sprite: {
            texture: imageUrl,
            // fit the piece's longer edge to roughly the circle's diameter
            xScale: (radius * 2.3) / Math.max(size.width, size.height),
            yScale: (radius * 2.3) / Math.max(size.width, size.height),
          },
        }
      : { fillStyle: color };
    const body = Bodies.circle(x, -20 - Math.random() * 60, radius, {
      restitution: 0.35,
      friction: 0.6,
      density: 0.002,
      render,
    });
    World.add(this.world, body);
    this.pieces.push({ candyId, body });
  }

  remove(candyId, count) {
    const { World } = this.Matter;
    let removed = 0;
    for (let i = this.pieces.length - 1; i >= 0 && removed < count; i--) {
      if (this.pieces[i].candyId === candyId) {
        World.remove(this.world, this.pieces[i].body);
        this.pieces.splice(i, 1);
        removed++;
      }
    }
  }

  resetToBag(items, piecesPerOz) {
    const { World } = this.Matter;
    this.pieces.forEach((p) => World.remove(this.world, p.body));
    this.pieces = [];
    Object.entries(items || {}).forEach(([candyId, oz]) => {
      this.drop(candyId, oz * piecesPerOz);
    });
  }

  destroy() {
    this.destroyed = true;
    const { Render, Runner, World } = this.Matter;
    Runner.stop(this.runner);
    Render.stop(this.render);
    World.clear(this.world, false);
  }
}

window.MixPhysics = MixPhysics;
